/**
 * WHAT: Completeness & Accuracy (C&A) report for SQL Server databases. Compares live
 *       sys.* catalog counts against the PostgreSQL snapshot stored in
 *       source_mssql_user_access so auditors can confirm the collection pipeline
 *       captured every principal, permission, and role membership.
 * WHY:  Parallels CAReport.js (Oracle) but uses MSSQL-specific metrics
 *       (server vs database principals, permissions by scope, role memberships).
 */
import React, { useState, useEffect } from 'react';
import API_CONFIG from '../config/api.config';
import { WideModal } from './common';
import './CAReport.css';

const METRIC_LABELS = {
  total_server_principals: 'Total Server Principals',
  server_principals_system: 'Server Principals (System)',
  server_principals_application: 'Server Principals (Application)',
  server_principals_disabled: 'Server Principals (Disabled)',
  total_database_principals: 'Total Database Principals',
  database_principals_system: 'Database Principals (System)',
  database_principals_application: 'Database Principals (Application)',
  total_server_permissions: 'Total Server Permissions',
  total_database_permissions: 'Total Database Permissions',
  total_server_role_memberships: 'Server Role Memberships',
  total_database_role_memberships: 'Database Role Memberships',
  sysadmin_count: 'Sysadmin Members',
};

const CAReportMSSQL = () => {
  const [databases, setDatabases] = useState([]);
  const [selectedDatabase, setSelectedDatabase] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorModal, setErrorModal] = useState({ show: false, title: '', message: '' });

  useEffect(() => {
    const fetchDatabases = async () => {
      try {
        const response = await fetch(API_CONFIG.getEndpoint('/api/databases/mssql-access/ca-databases/'));
        const data = await response.json();
        setDatabases(data.databases || []);
        if (data.databases && data.databases.length > 0) {
          setSelectedDatabase(data.databases[0].id);
        }
      } catch (err) {
        console.error('Error fetching MSSQL databases:', err);
        setError('Failed to load MSSQL databases');
      }
    };
    fetchDatabases();
  }, []);

  const generateReport = async () => {
    if (!selectedDatabase) {
      setError('Please select a database');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_CONFIG.getEndpoint('/api/databases/ca-report/generate-mssql/'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ database_id: selectedDatabase }),
      });

      let data = null;
      try {
        data = await response.json();
      } catch (_) { /* ignore */ }

      if (!response.ok) {
        const backendMessage = (data && data.error) || `HTTP ${response.status}`;
        const errorCode = data && data.error_code;
        const titleMap = {
          no_connection: 'No Connection Configured',
          connection_inactive: 'Connection Inactive',
          vault_password_missing: 'Vault Password Missing',
          connection_failed: 'SQL Server Connection Failed',
          not_found: 'Database Not Found',
          inactive: 'Database Inactive',
        };
        setErrorModal({
          show: true,
          title: titleMap[errorCode] || 'C&A Report Error',
          message: backendMessage,
        });
        return;
      }

      setReportData(data);
    } catch (err) {
      console.error('Error generating MSSQL C&A report:', err);
      setErrorModal({
        show: true,
        title: 'C&A Report Error',
        message: err.message || 'Failed to generate C&A report.',
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const calculateMatchRate = () => {
    if (!reportData?.comparison) return 0;
    const metrics = Object.keys(reportData.comparison);
    if (metrics.length === 0) return 0;
    const matchCount = metrics.filter(k => reportData.comparison[k].status === 'MATCH').length;
    return ((matchCount / metrics.length) * 100).toFixed(1);
  };

  const renderMetricRow = (key, label, metric) => (
    <tr key={key} className={metric.status === 'MATCH' ? 'match' : 'mismatch'}>
      <td className="metric-label">{label}</td>
      <td className="metric-value">{(metric.mssql ?? 0).toLocaleString()}</td>
      <td className="metric-value">{(metric.postgresql ?? 0).toLocaleString()}</td>
      <td className="metric-value">{(metric.difference ?? 0).toLocaleString()}</td>
      <td className="metric-value">{(metric.match_percentage ?? 0).toFixed(2)}%</td>
      <td className={`metric-status status-${metric.status.toLowerCase()}`}>
        {metric.status}
      </td>
    </tr>
  );

  const renderPrincipalDifferences = (metricKey, title) => {
    const metric = reportData?.comparison?.[metricKey];
    if (!metric?.detailed_analysis) return null;
    const { missing_in_uar = [], extra_in_uar = [], missing_count, extra_count } = metric.detailed_analysis;
    if (missing_count === 0 && extra_count === 0) return null;

    return (
      <div className="comparison-section" key={`diff-${metricKey}`}>
        <h3>{title} — Differences</h3>
        {missing_count > 0 && (
          <>
            <h4 style={{ color: '#b00', marginTop: '12px' }}>
              <i className="fas fa-exclamation-triangle me-2"></i>
              Missing in UAR Application ({missing_count})
            </h4>
            <p className="section-description">
              These principals exist in SQL Server but are absent from the latest snapshot.
            </p>
            <table className="ca-table">
              <thead>
                <tr>
                  <th>Name</th><th>Type</th><th>System</th><th>Disabled / Fixed Role</th>
                </tr>
              </thead>
              <tbody>
                {missing_in_uar.map((p, i) => (
                  <tr key={i}>
                    <td className="username-col">{p.name}</td>
                    <td>{p.type}</td>
                    <td>{p.is_system ? 'Yes' : 'No'}</td>
                    <td>{p.is_disabled ? 'Disabled' : (p.is_fixed_role ? 'Fixed role' : '—')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        {extra_count > 0 && (
          <>
            <h4 style={{ color: '#a60', marginTop: '12px' }}>
              <i className="fas fa-question-circle me-2"></i>
              Extra in UAR Application ({extra_count})
            </h4>
            <p className="section-description">
              These principals exist in the snapshot but not live — likely dropped after the last collection.
            </p>
            <table className="ca-table">
              <thead>
                <tr>
                  <th>Name</th><th>Type</th><th>System</th><th>Disabled / Fixed Role</th>
                </tr>
              </thead>
              <tbody>
                {extra_in_uar.map((p, i) => (
                  <tr key={i}>
                    <td className="username-col">{p.name}</td>
                    <td>{p.type}</td>
                    <td>{p.is_system ? 'Yes' : 'No'}</td>
                    <td>{p.is_disabled ? 'Disabled' : (p.is_fixed_role ? 'Fixed role' : '—')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    );
  };

  const renderSampleUsers = () => {
    const live = reportData?.sample_users_mssql || [];
    const pg = reportData?.sample_users_postgresql || [];
    if (live.length === 0) return null;
    const pgByName = Object.fromEntries(pg.map(u => [u.principal_name, u]));

    return (
      <div className="comparison-section">
        <h2>Sample Principal Deep-Dive</h2>
        <p className="section-description">
          Up to 5 random application server principals compared side-by-side between live SQL Server and the UAR snapshot.
        </p>
        {live.map((u, i) => {
          const snap = pgByName[u.principal_name] || {};
          return (
            <div key={i} className="sample-user-card" style={{ marginBottom: '18px', padding: '12px', border: '1px solid #ddd', borderRadius: '6px' }}>
              <h4 style={{ marginBottom: '8px' }}>
                <i className="fas fa-user me-2"></i>{u.principal_name}
              </h4>
              <table className="ca-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>SQL Server (live)</th>
                    <th>UAR Snapshot</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="metric-label">Server Permissions</td>
                    <td className="metric-value">{(u.server_permissions || []).length}</td>
                    <td className="metric-value">{(snap.server_permissions || []).length}</td>
                  </tr>
                  <tr>
                    <td className="metric-label">Database Permissions</td>
                    <td className="metric-value">{(u.database_permissions || []).length}</td>
                    <td className="metric-value">{(snap.database_permissions || []).length}</td>
                  </tr>
                  <tr>
                    <td className="metric-label">Server Role Memberships</td>
                    <td className="metric-value">{(u.server_role_memberships || []).join(', ') || '—'}</td>
                    <td className="metric-value">{(snap.server_role_memberships || []).join(', ') || '—'}</td>
                  </tr>
                  <tr>
                    <td className="metric-label">Database Role Memberships</td>
                    <td className="metric-value">{(u.database_role_memberships || []).join(', ') || '—'}</td>
                    <td className="metric-value">{(snap.database_role_memberships || []).join(', ') || '—'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    );
  };

  const groups = [
    {
      title: 'Server Principals',
      keys: ['total_server_principals', 'server_principals_system',
             'server_principals_application', 'server_principals_disabled'],
    },
    {
      title: 'Database Principals',
      keys: ['total_database_principals', 'database_principals_system',
             'database_principals_application'],
    },
    {
      title: 'Permissions',
      keys: ['total_server_permissions', 'total_database_permissions'],
    },
    {
      title: 'Role Memberships',
      keys: ['total_server_role_memberships', 'total_database_role_memberships', 'sysadmin_count'],
    },
  ];

  return (
    <div className="ca-report-container">
      <div className="ca-header">
        <h1>Completeness & Accuracy Report — SQL Server</h1>
        <p className="ca-description">
          Validates that data extraction from source SQL Server databases is complete and accurate
          by comparing counts between the live instance and the UAR snapshot.
        </p>
      </div>

      <div className="ca-controls">
        <div className="control-group">
          <label htmlFor="database-select">Select Database:</label>
          <select
            id="database-select"
            value={selectedDatabase}
            onChange={(e) => setSelectedDatabase(e.target.value)}
            disabled={loading}
          >
            <option value="">-- Select Database --</option>
            {databases.map(db => (
              <option key={db.id} value={db.id}>
                {db.db_name} ({db.server_name || db.host_name})
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={generateReport}
          disabled={loading || !selectedDatabase}
          className="generate-button"
        >
          {loading ? 'Generating...' : 'Generate Report'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      <WideModal
        show={errorModal.show}
        onClose={() => setErrorModal({ show: false, title: '', message: '' })}
        title={errorModal.title}
        headerIcon="fa-exclamation-triangle"
        headerClass="bg-danger text-white"
        maxWidth="600px"
        footer={
          <button
            className="btn btn-secondary"
            onClick={() => setErrorModal({ show: false, title: '', message: '' })}
          >
            Close
          </button>
        }
      >
        <div style={{ padding: '10px 5px', fontSize: '15px', lineHeight: '1.6' }}>
          {errorModal.message}
        </div>
      </WideModal>

      {reportData && (
        <div className="report-content">
          <div className="report-summary">
            <div className="summary-card">
              <h3>Report Information</h3>
              <div className="summary-details">
                <div className="summary-item">
                  <span className="summary-label">Database:</span>
                  <span className="summary-value">{reportData.database_name}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Report Generated:</span>
                  <span className="summary-value">{formatDate(reportData.report_generated_at)}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Last Data Collection:</span>
                  <span className="summary-value">{formatDate(reportData.last_collection_timestamp)}</span>
                </div>
              </div>
            </div>

            <div className="summary-card match-rate">
              <h3>Overall Match Rate</h3>
              <div className="match-rate-value">{calculateMatchRate()}%</div>
              <div className="match-rate-label">
                {Object.keys(reportData.comparison).filter(k => reportData.comparison[k].status === 'MATCH').length} of {Object.keys(reportData.comparison).length} metrics matched
              </div>
            </div>
          </div>

          {reportData.connection_details && (
            <div className="connection-details-section">
              <h2>
                <i className="fas fa-plug me-2"></i>
                Source Database Connection Verification
              </h2>
              <p className="connection-subtitle">
                Live connection established to source SQL Server for real-time data validation.
              </p>
              <div className="connection-grid">
                <div className="connection-item">
                  <span className="connection-icon"><i className="fas fa-check-circle"></i></span>
                  <div className="connection-info">
                    <span className="connection-label">Connection Status</span>
                    <span className="connection-value success">Successfully Connected</span>
                  </div>
                </div>
                <div className="connection-item">
                  <span className="connection-icon"><i className="fas fa-database"></i></span>
                  <div className="connection-info">
                    <span className="connection-label">Database Name</span>
                    <span className="connection-value">{reportData.connection_details.database_name}</span>
                  </div>
                </div>
                <div className="connection-item">
                  <span className="connection-icon"><i className="fas fa-server"></i></span>
                  <div className="connection-info">
                    <span className="connection-label">Instance Name</span>
                    <span className="connection-value">{reportData.connection_details.instance_name}</span>
                  </div>
                </div>
                <div className="connection-item">
                  <span className="connection-icon"><i className="fas fa-desktop"></i></span>
                  <div className="connection-info">
                    <span className="connection-label">Host Name</span>
                    <span className="connection-value">{reportData.connection_details.host_name}</span>
                  </div>
                </div>
                <div className="connection-item">
                  <span className="connection-icon"><i className="fas fa-code-branch"></i></span>
                  <div className="connection-info">
                    <span className="connection-label">Edition</span>
                    <span className="connection-value">{reportData.connection_details.edition}</span>
                  </div>
                </div>
                <div className="connection-item">
                  <span className="connection-icon"><i className="fas fa-info-circle"></i></span>
                  <div className="connection-info">
                    <span className="connection-label">Product Version</span>
                    <span className="connection-value">{reportData.connection_details.product_version}</span>
                  </div>
                </div>
                <div className="connection-item">
                  <span className="connection-icon"><i className="fas fa-user"></i></span>
                  <div className="connection-info">
                    <span className="connection-label">Connected As</span>
                    <span className="connection-value">{reportData.connection_details.connected_as_user}</span>
                  </div>
                </div>
                <div className="connection-item">
                  <span className="connection-icon"><i className="fas fa-link"></i></span>
                  <div className="connection-info">
                    <span className="connection-label">Connection String</span>
                    <span className="connection-value monospace">{reportData.connection_details.connection_string}</span>
                  </div>
                </div>
                <div className="connection-item">
                  <span className="connection-icon"><i className="fas fa-tachometer-alt"></i></span>
                  <div className="connection-info">
                    <span className="connection-label">Connection Duration</span>
                    <span className="connection-value">{reportData.connection_details.connection_duration_seconds}s</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {groups.map(group => {
            const hasAny = group.keys.some(k => reportData.comparison[k] !== undefined);
            if (!hasAny) return null;
            return (
              <div className="comparison-section" key={group.title}>
                <h2>{group.title}</h2>
                <table className="ca-table">
                  <thead>
                    <tr>
                      <th className="metric-name-col">Metric</th>
                      <th className="number-col">SQL Server</th>
                      <th className="number-col">UAR Snapshot</th>
                      <th className="number-col">Difference</th>
                      <th className="number-col">Match %</th>
                      <th className="status-col">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.keys.map(k => {
                      const m = reportData.comparison[k];
                      if (!m) return null;
                      return renderMetricRow(k, METRIC_LABELS[k] || k, m);
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}

          {renderPrincipalDifferences('total_server_principals', 'Server Principals')}
          {renderPrincipalDifferences('total_database_principals', 'Database Principals')}

          {renderSampleUsers()}
        </div>
      )}
    </div>
  );
};

export default CAReportMSSQL;
